import { defineComponent, PropType } from 'vue';
import styles from './index.module.less';
import { dayjs } from 'element-plus';
import clsx from 'clsx';

interface FaultSource {
  frameId: string;
  moduleName: string;
  errorCodeString: string;
}

interface AbnormalInfo {
  ip: string;
  errorCode: string;
  errorLevel: string;
  errorCodeString: string;
  errorMsg: string;
  timestamp: number;
  moduleName?: string;
  faultSource?: FaultSource[];
}

const props = {
  errList: {
    type: Array as PropType<AbnormalInfo[]>,
    default: [],
  },
  showDetail: {
    type: Boolean,
    default: false,
  },
};

/**
 * 后端返回的 JSON 可能是小驼峰（moduleName），
 * 也可能是 proto 原始下划线（module_name），这里统一取值。
 */
function pickString(obj: any, camelKey: string, snakeKey: string): string {
  return obj?.[camelKey] ?? obj?.[snakeKey] ?? '-';
}

/**
 * 兼容 proto 定义的 repeated FaultSource fault_source = 9：
 * 后端返回的 JSON 可能是小驼峰（faultSource/frameId），
 * 也可能是原始下划线（fault_source/frame_id），这里统一取值。
 */
function normalizeFaultSource(item: AbnormalInfo): FaultSource[] {
  const raw = (item as any)?.faultSource ?? (item as any)?.fault_source;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((source: any) => ({
    frameId: pickString(source, 'frameId', 'frame_id'),
    moduleName: pickString(source, 'moduleName', 'module_name'),
    errorCodeString: pickString(source, 'errorCodeString', 'error_code_string'),
  }));
}

const VarguardItem = defineComponent({
  name: 'VarguardItem',
  props,
  setup(props) {
    return {
      props,
    };
  },
  render() {
    return (
      <div>
        {this.props.errList.map((item, index) => {
          const faultSourceList = normalizeFaultSource(item);
          return (
            <div
              class={clsx(styles['error-item'], {
                [styles['show-detail']]: this.props.showDetail,
              })}
              key={index}
            >
              <div class={styles.row}>
                <span class={styles.detail}>
                  时间：{dayjs(item.timestamp / 1000000).format('HH:mm:ss')}
                </span>
                <span class={styles.detail}>IP：{item.ip}</span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>
                  模块：{pickString(item, 'moduleName', 'module_name')}
                </span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>异常编码：{item.errorCode}</span>
                <span class={styles.detail}>异常级别：{item.errorLevel}</span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>
                  ErrorCode：{item.errorCodeString}
                </span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>Message：{item.errorMsg}</span>
              </div>
              {!!faultSourceList.length && (
                <div class={styles['fault-source']}>
                  <div class={styles['fault-source-title']}>
                    故障源（{faultSourceList.length}）：
                  </div>
                  {faultSourceList.map((source, sourceIndex) => (
                    <div class={styles['fault-source-item']} key={sourceIndex}>
                      <div class={styles.row}>
                        <span class={styles.detail}>
                          FrameId：{source.frameId}
                        </span>
                        <span class={styles.detail}>
                          Module：{source.moduleName}
                        </span>
                      </div>
                      <div class={styles.row}>
                        <span class={styles.detail}>
                          ErrorCode：{source.errorCodeString}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  },
});

export default VarguardItem;
